import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, X, FileText, Plus } from "lucide-react";
import api, { apiError, PAYMENT_METHODS } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { fmtTRY } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);

export default function NewExpense() {
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [form, setForm] = useState({
    expense_date: today(), amount: "", description: "", trade_id: "",
    supplier: "", payment_method: "", observations: "",
  });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // inline trade creation
  const [newTradeOpen, setNewTradeOpen] = useState(false);
  const [newTradeName, setNewTradeName] = useState("");

  const loadTrades = () => api.get("/trades").then((res) => setTrades(res.data));
  useEffect(() => { loadTrades(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, data]);
      }
      toast.success("Dosya(lar) yüklendi");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const validate = () => {
    if (!form.expense_date) return "Gider tarihi zorunludur";
    if (!form.amount || Number(form.amount) <= 0) return "Geçerli bir tutar girin";
    if (!form.description.trim()) return "Açıklama zorunludur";
    if (!form.trade_id) return "İş kalemi seçiniz";
    if (!form.supplier.trim()) return "Tedarikçi / lehtar zorunludur";
    if (!form.payment_method) return "Ödeme yöntemi seçiniz";
    return null;
  };

  const openConfirm = () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setConfirmOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/expenses", {
        ...form,
        amount: Number(form.amount),
        attachments: attachments.map(({ id, storage_path, original_filename, content_type, size }) =>
          ({ id, storage_path, original_filename, content_type, size })),
      });
      toast.success("Gider kaydedildi");
      navigate("/expenses");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const createTrade = async () => {
    if (!newTradeName.trim()) return;
    try {
      const { data } = await api.post("/trades", { name: newTradeName.trim() });
      await loadTrades();
      set("trade_id", data.id);
      setNewTradeName("");
      setNewTradeOpen(false);
      toast.success("İş kalemi eklendi");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-2xl animate-fade-up">
      <PageHeader overline="Gider" title="Yeni Gider" subtitle="Yeni bir şantiye gideri kaydedin" />

      <div className="space-y-5 rounded-md border border-border bg-card p-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Gider Tarihi *</Label>
            <Input type="date" value={form.expense_date} onChange={(e) => set("expense_date", e.target.value)} data-testid="expense-date-input" />
          </div>
          <div className="space-y-2">
            <Label>Tutar (₺) *</Label>
            <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0,00" data-testid="expense-amount-input" />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Açıklama *</Label>
          <Input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Örn: Çimento alımı" data-testid="expense-description-input" />
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>İş Kalemi *</Label>
              <button type="button" onClick={() => setNewTradeOpen(true)} className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary" data-testid="add-trade-inline-button">
                <Plus className="h-3 w-3" /> Yeni
              </button>
            </div>
            <Select value={form.trade_id} onValueChange={(v) => set("trade_id", v)}>
              <SelectTrigger data-testid="expense-trade-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
              <SelectContent>
                {trades.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Ödeme Yöntemi *</Label>
            <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
              <SelectTrigger data-testid="expense-payment-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Tedarikçi / Lehtar *</Label>
          <Input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} placeholder="Örn: XYZ Yapı Market" data-testid="expense-supplier-input" />
        </div>

        <div className="space-y-2">
          <Label>Gözlemler / Notlar</Label>
          <Textarea value={form.observations} onChange={(e) => set("observations", e.target.value)} placeholder="Opsiyonel" rows={2} data-testid="expense-observations-input" />
        </div>

        {/* Attachments */}
        <div className="space-y-2">
          <Label>Ekler (fatura, fotoğraf, PDF...)</Label>
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-secondary/40 px-4 py-6 text-sm text-muted-foreground transition-colors hover:bg-secondary" data-testid="expense-upload-label">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Yükleniyor..." : "Dosya seçmek için tıklayın"}
            <input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="expense-file-input" />
          </label>
          {attachments.length > 0 && (
            <div className="space-y-2 pt-1">
              {attachments.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm">
                  <span className="flex min-w-0 items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                    <span className="truncate">{a.original_filename}</span>
                  </span>
                  <button onClick={() => removeAttachment(a.id)} className="text-slate-400 hover:text-destructive" data-testid={`remove-attachment-${a.id}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => navigate("/expenses")} data-testid="expense-cancel-button">İptal</Button>
          <Button onClick={openConfirm} data-testid="expense-submit-button">Kaydet</Button>
        </div>
      </div>

      {/* Confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="expense-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Onaylıyor musunuz?</DialogTitle>
            <DialogDescription>Aşağıdaki gideri kaydetmek istediğinizden emin misiniz?</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 rounded-md bg-secondary/50 p-4 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Tutar</span><span className="font-semibold tabular-nums">{fmtTRY(Number(form.amount))}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Açıklama</span><span className="font-medium">{form.description}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">İş Kalemi</span><span>{trades.find((t) => t.id === form.trade_id)?.name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tedarikçi</span><span>{form.supplier}</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="confirm-cancel-button">Vazgeç</Button>
            <Button onClick={save} disabled={saving} data-testid="confirm-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Onayla ve Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline trade dialog */}
      <Dialog open={newTradeOpen} onOpenChange={setNewTradeOpen}>
        <DialogContent data-testid="new-trade-dialog">
          <DialogHeader>
            <DialogTitle>Yeni İş Kalemi</DialogTitle>
            <DialogDescription>Yeni bir iş kalemi oluşturun.</DialogDescription>
          </DialogHeader>
          <Input value={newTradeName} onChange={(e) => setNewTradeName(e.target.value)} placeholder="Örn: İzolasyon" data-testid="new-trade-name-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewTradeOpen(false)}>İptal</Button>
            <Button onClick={createTrade} data-testid="new-trade-save-button">Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
